/**
 * Contrat entre l'ecran de vente (PR 1.5a) et le paiement comptant (PR 1.5b).
 *
 * CE FICHIER EST IDENTIQUE, OCTET POUR OCTET, DANS LES DEUX PR. On ne le
 * modifie dans aucune des deux : un changement passe par une PR de suivi qui
 * le change partout a la fois (verifie par sha256 a la reconciliation).
 *
 * L'app est en JavaScript (Babel + Jest, sans TypeScript) : les types sont
 * decrits en JSDoc, et verifierPanier() les controle a l'execution.
 *
 * Qui fait quoi :
 *   - 1.5a construit le PanierAEncaisser (lignes, rabais, prix modifies,
 *     taxes, totaux) et le passe au paiement par onEncaisser(panier).
 *   - 1.5b le recoit tel quel : il ne recalcule JAMAIS les taxes ni les
 *     lignes. Il ajoute l'arrondi du comptant, le montant recu, la monnaie,
 *     le quart (shift_id) et le numero local, puis enregistre la
 *     VenteEnregistree dans le magasin IndexedDB `sales` (cle sale_uuid).
 *
 * Tous les montants sont des ENTIERS EN CENTS (Number.isSafeInteger), hors
 * taxe sauf mention contraire. Les poids sont des entiers en grammes.
 * Les cles suivent le schema IndexedDB (plan 1.2), les tables
 * pos_caisse_sale / pos_caisse_sale_line (plan 1.3) et le journal (plan 1.5).
 */

export const CONTRAT_VENTE_VERSION = 1;

/** Memes valeurs que sync/catalogSync.js TAX_CODES (catalogue de la PR 1.2). */
export const TAX_CODES = Object.freeze(['NONE', 'TPS', 'TPS_TVQ']);

/** Taux en millioniemes : TPS 5 %, TVQ 9,975 %. */
export const TAUX_TAXES = Object.freeze({ TPS_PPM: 50000, TVQ_PPM: 99750 });

/**
 * Regle d'arrondi des taxes de la vente. Observee sur 50 vraies ventes
 * Lightspeed (fixture vente/__fixtures__/ventes-reelles-50.json) : la taxe
 * totale = arrondi au cent de la SOMME des taxes non arrondies de toutes les
 * lignes (50 sur 50). La PR 1.5a confirme ou corrige cette valeur avec ses
 * tests ; la PR 1.5b ne s'en sert pas pour calculer, seulement pour l'afficher.
 */
export const METHODES_TAXES = Object.freeze({
    LIGHTSPEED_SOMME_NON_ARRONDIE: 'LIGHTSPEED_SOMME_NON_ARRONDIE',
    PAR_LIGNE: 'PAR_LIGNE',
});

export const MODES_LIGNE = Object.freeze({ UNITE: 'UNITE', POIDS: 'POIDS' });

export const TYPES_RABAIS = Object.freeze({ POURCENT: 'POURCENT', MONTANT: 'MONTANT' });

/** Rabais saisi sur une ligne, ou sur toute la vente puis reparti sur les lignes. */
export const PORTEES_RABAIS = Object.freeze({ LIGNE: 'LIGNE', VENTE: 'VENTE' });

/**
 * @typedef {object} Responsable
 * @property {number} employee_id employe qui a autorise (son NIP)
 * @property {string} display_name prenom affiche (« Rabais 10 % (Marie) »)
 */

/**
 * @typedef {object} RabaisLigne
 * @property {'POURCENT'|'MONTANT'} type
 * @property {number} valeur POURCENT : points de base (1000 = 10 %) ; MONTANT : cents
 * @property {'LIGNE'|'VENTE'} portee
 * @property {number} montant_cents rabais effectif sur la ligne, en cents (> 0)
 * @property {Responsable} par employe qui a fait le rabais
 * @property {Responsable|null} autorise_par responsable dont le NIP a ete demande (au-dela du plafond), sinon null
 */

/**
 * @typedef {object} PrixModifie
 * @property {number} prix_cents nouveau prix unitaire (ou au kg) hors taxe
 * @property {number} prix_catalogue_cents prix du catalogue remplace
 * @property {Responsable} par responsable dont le NIP a autorise le prix
 * @property {string} at ISO 8601 local
 */

/**
 * @typedef {object} LigneVente
 * @property {number} line_no 1, 2, 3... sans trou, dans l'ordre d'ajout
 * @property {string} productcode cle du catalogue
 * @property {string|null} lightspeed_product_id item.lightspeed_id du catalogue
 * @property {string} name nom affiche et imprime
 * @property {string|null} sku
 * @property {string|null} scanned_code code lu au scan (null si ajout par la recherche)
 * @property {'UNITE'|'POIDS'} mode
 * @property {number} qty UNITE : entier >= 1 ; POIDS : toujours 1
 * @property {number|null} weight_g POIDS : poids en grammes s'il est connu, sinon null ; UNITE : null
 * @property {number} unit_price_cents prix applique (apres prix modifie) par unite, ou par kg en POIDS
 * @property {number} catalog_price_cents prix du catalogue (par unite ou par kg)
 * @property {PrixModifie|null} override
 * @property {RabaisLigne|null} discount
 * @property {number} gross_cents montant avant rabais (UNITE : unit_price_cents x qty ; POIDS : prix du code-barres)
 * @property {number} discount_cents 0 sans rabais
 * @property {number} net_cents gross_cents - discount_cents, base taxable de la ligne
 * @property {'NONE'|'TPS'|'TPS_TVQ'} tax_code
 * @property {number} tps_cents TPS de la ligne arrondie au cent (affichage et ERP)
 * @property {number} tvq_cents TVQ de la ligne arrondie au cent
 * @property {number} line_total_cents net_cents + tps_cents + tvq_cents
 */

/**
 * @typedef {object} TotauxVente
 * @property {number} gross_cents somme des gross_cents
 * @property {number} discount_cents somme des discount_cents
 * @property {number} subtotal_cents somme des net_cents (hors taxe)
 * @property {number} tps_cents TPS FACTUREE (selon methode_taxes)
 * @property {number} tvq_cents TVQ FACTUREE
 * @property {number} tax_cents tps_cents + tvq_cents
 * @property {number} total_cents subtotal_cents + tax_cents, AVANT l'arrondi du comptant
 * @property {number} item_count nombre d'articles (somme des qty)
 *
 * Attention : la somme des taxes de ligne peut differer de tps_cents ou
 * tvq_cents de quelques cents (arrondi). Les totaux font foi ; le paiement et
 * le recu n'utilisent que les totaux.
 */

/**
 * @typedef {object} PanierAEncaisser
 * Ce que l'ecran de vente transmet au paiement. Fige : le paiement ne le
 * modifie pas. « Retour a la vente » rend le meme objet a l'ecran de vente.
 * @property {1} contrat_version
 * @property {string} sale_uuid UUID v4 cree par l'ecran de vente a la PREMIERE ligne, garde tant que le panier vit : cle d'idempotence (un double clic, un retour a la vente puis un nouvel encaissement gardent le meme sale_uuid ; le paiement n'ecrit jamais deux ventes pour un meme sale_uuid)
 * @property {number} terminal_id
 * @property {number} store_id
 * @property {number} employee_id caissier connecte par NIP
 * @property {string} employee_display_name
 * @property {string|null} catalog_snapshot_id instantane du catalogue utilise
 * @property {string} created_at ISO 8601 local, ajout de la premiere ligne
 * @property {string} methode_taxes une valeur de METHODES_TAXES
 * @property {LigneVente[]} lines au moins une ligne
 * @property {TotauxVente} totals
 */

/**
 * @typedef {object} PaiementComptant
 * Ajoute par la PR 1.5b.
 * @property {'COMPTANT'} mode
 * @property {number} cash_rounding_cents arrondi a 0,05 $ (signe : -2 a +2)
 * @property {number} amount_due_cents totals.total_cents + cash_rounding_cents
 * @property {number} cash_received_cents >= amount_due_cents
 * @property {number} change_given_cents cash_received_cents - amount_due_cents
 */

/**
 * @typedef {object} VenteEnregistree
 * Enregistrement du magasin IndexedDB `sales` (cle sale_uuid), ecrit par la
 * PR 1.5b en UNE transaction. Reprend le panier tel quel, plus :
 * @property {string} shift_id quart ouvert (magasin `shifts`)
 * @property {string} local_number ex. S03-P1-000123 (unique, sans trou)
 * @property {PaiementComptant} payment
 * @property {'completed'} status
 * @property {string} completed_at ISO 8601 local
 * @property {number|null} journal_seq null jusqu'a la PR 2.1
 * @property {number} receipt_print_count impressions du recu (reimpression : +1)
 */

/** Etapes du parcours de caisse, apres la connexion par NIP. */
export const ETAPES_CAISSE = Object.freeze({
    OUVERTURE: 'OUVERTURE', // aucun quart ouvert : fond de caisse (1.5b)
    VENTE: 'VENTE', // ecran de vente (1.5a)
    PAIEMENT: 'PAIEMENT', // paiement comptant (1.5b)
    RECU: 'RECU', // vente enregistree, monnaie, reimpression (1.5b)
});

export const ACTIONS_CAISSE = Object.freeze({
    QUART_ABSENT: 'QUART_ABSENT',
    QUART_OUVERT: 'QUART_OUVERT',
    ENCAISSER: 'ENCAISSER',
    RETOUR_VENTE: 'RETOUR_VENTE',
    VENTE_TERMINEE: 'VENTE_TERMINEE',
    NOUVELLE_VENTE: 'NOUVELLE_VENTE',
});

/** Etat initial : on ne sait pas encore si un quart est ouvert. */
export const ETAT_CAISSE_INITIAL = Object.freeze({
    etape: ETAPES_CAISSE.OUVERTURE, shift_id: null, panier: null, vente: null,
});

/**
 * Point d'entree de la navigation vente -> paiement : un reducteur pur, le
 * meme pour les deux PR.
 *   QUART_ABSENT                          -> OUVERTURE
 *   QUART_OUVERT { shift_id }             -> VENTE
 *   ENCAISSER { panier } (depuis VENTE)   -> PAIEMENT, panier fige
 *   RETOUR_VENTE (depuis PAIEMENT)        -> VENTE, meme panier rendu a l'ecran de vente
 *   VENTE_TERMINEE { vente } (PAIEMENT)   -> RECU
 *   NOUVELLE_VENTE (depuis RECU)          -> VENTE, panier vide
 * Toute autre transition est ignoree (etat inchange) : un double clic sur
 * « Encaisser » ou « Terminer la vente » ne fait rien de plus.
 */
export function fluxCaisse(state, action) {
    const s = state || ETAT_CAISSE_INITIAL;
    if (!action || typeof action.type !== 'string') return s;
    switch (action.type) {
    case ACTIONS_CAISSE.QUART_ABSENT:
        return { ...ETAT_CAISSE_INITIAL };
    case ACTIONS_CAISSE.QUART_OUVERT:
        if (typeof action.shift_id !== 'string' || action.shift_id === '') return s;
        if (s.etape !== ETAPES_CAISSE.OUVERTURE && s.shift_id === action.shift_id) return s;
        return { etape: ETAPES_CAISSE.VENTE, shift_id: action.shift_id, panier: null, vente: null };
    case ACTIONS_CAISSE.ENCAISSER:
        if (s.etape !== ETAPES_CAISSE.VENTE || verifierPanier(action.panier).length > 0) return s;
        return { ...s, etape: ETAPES_CAISSE.PAIEMENT, panier: action.panier, vente: null };
    case ACTIONS_CAISSE.RETOUR_VENTE:
        if (s.etape !== ETAPES_CAISSE.PAIEMENT) return s;
        return { ...s, etape: ETAPES_CAISSE.VENTE };
    case ACTIONS_CAISSE.VENTE_TERMINEE:
        if (s.etape !== ETAPES_CAISSE.PAIEMENT || !action.vente || !s.panier
            || action.vente.sale_uuid !== s.panier.sale_uuid) return s;
        return { ...s, etape: ETAPES_CAISSE.RECU, panier: null, vente: action.vente };
    case ACTIONS_CAISSE.NOUVELLE_VENTE:
        if (s.etape !== ETAPES_CAISSE.RECU) return s;
        return { ...s, etape: ETAPES_CAISSE.VENTE, panier: null, vente: null };
    default:
        return s;
    }
}

/**
 * Vrai quand une mise a jour de l'app doit attendre (UpdatePrompt blocked) :
 * paiement en cours, ou panier non vide a l'ecran de vente (panierNonVide
 * est fourni par l'ecran de vente).
 */
export function venteEnCours(state, panierNonVide = false) {
    const s = state || ETAT_CAISSE_INITIAL;
    return s.etape === ETAPES_CAISSE.PAIEMENT || (s.etape === ETAPES_CAISSE.VENTE && panierNonVide === true);
}

const UUID_V4 = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/;

function entier(v) {
    return Number.isSafeInteger(v);
}

function entierPositif(v) {
    return Number.isSafeInteger(v) && v >= 0;
}

/**
 * Controle un PanierAEncaisser. Renvoie la liste des erreurs ([] = valide).
 * Appele par 1.5a avant onEncaisser et par 1.5b a la reception.
 * @param {PanierAEncaisser} panier
 * @returns {string[]}
 */
export function verifierPanier(panier) {
    const e = [];
    if (!panier || typeof panier !== 'object') return ['panier absent'];
    if (panier.contrat_version !== CONTRAT_VENTE_VERSION) e.push('contrat_version');
    if (typeof panier.sale_uuid !== 'string' || !UUID_V4.test(panier.sale_uuid)) e.push('sale_uuid');
    for (const k of ['terminal_id', 'store_id', 'employee_id']) if (!entier(panier[k])) e.push(k);
    if (typeof panier.employee_display_name !== 'string') e.push('employee_display_name');
    if (typeof panier.created_at !== 'string') e.push('created_at');
    if (!Object.values(METHODES_TAXES).includes(panier.methode_taxes)) e.push('methode_taxes');
    const lines = panier.lines;
    if (!Array.isArray(lines) || lines.length === 0) {
        e.push('lines');
        return e;
    }
    const somme = { gross: 0, discount: 0, net: 0, items: 0 };
    lines.forEach((l, i) => {
        const p = `lines[${i}]`;
        if (!l || typeof l !== 'object') {
            e.push(p);
            return;
        }
        if (l.line_no !== i + 1) e.push(`${p}.line_no`);
        if (typeof l.productcode !== 'string' || l.productcode === '') e.push(`${p}.productcode`);
        if (typeof l.name !== 'string') e.push(`${p}.name`);
        if (!Object.values(MODES_LIGNE).includes(l.mode)) e.push(`${p}.mode`);
        if (l.mode === MODES_LIGNE.UNITE && (!entier(l.qty) || l.qty < 1)) e.push(`${p}.qty`);
        if (l.mode === MODES_LIGNE.POIDS && l.qty !== 1) e.push(`${p}.qty`);
        if (l.weight_g !== null && !(entier(l.weight_g) && l.weight_g > 0)) e.push(`${p}.weight_g`);
        if (!TAX_CODES.includes(l.tax_code)) e.push(`${p}.tax_code`);
        for (const k of ['unit_price_cents', 'catalog_price_cents', 'gross_cents', 'discount_cents', 'net_cents',
            'tps_cents', 'tvq_cents', 'line_total_cents']) {
            if (!entierPositif(l[k])) e.push(`${p}.${k}`);
        }
        if (l.net_cents !== l.gross_cents - l.discount_cents) e.push(`${p}.net_cents`);
        if (l.line_total_cents !== l.net_cents + l.tps_cents + l.tvq_cents) e.push(`${p}.line_total_cents`);
        if (l.tax_code === 'NONE' && (l.tps_cents !== 0 || l.tvq_cents !== 0)) e.push(`${p}.tax_code`);
        if (l.tax_code === 'TPS' && l.tvq_cents !== 0) e.push(`${p}.tax_code`);
        if ((l.discount_cents > 0) !== Boolean(l.discount)) e.push(`${p}.discount`);
        if (l.override && !(l.override.par && entier(l.override.par.employee_id))) e.push(`${p}.override`);
        somme.gross += l.gross_cents;
        somme.discount += l.discount_cents;
        somme.net += l.net_cents;
        somme.items += l.qty;
    });
    const t = panier.totals;
    if (!t || typeof t !== 'object') {
        e.push('totals');
        return e;
    }
    for (const k of ['gross_cents', 'discount_cents', 'subtotal_cents', 'tps_cents', 'tvq_cents', 'tax_cents',
        'total_cents', 'item_count']) {
        if (!entierPositif(t[k])) e.push(`totals.${k}`);
    }
    if (t.gross_cents !== somme.gross) e.push('totals.gross_cents');
    if (t.discount_cents !== somme.discount) e.push('totals.discount_cents');
    if (t.subtotal_cents !== somme.net) e.push('totals.subtotal_cents');
    if (t.item_count !== somme.items) e.push('totals.item_count');
    if (t.tax_cents !== t.tps_cents + t.tvq_cents) e.push('totals.tax_cents');
    if (t.total_cents !== t.subtotal_cents + t.tax_cents) e.push('totals.total_cents');
    return e;
}

/**
 * Montant en format quebecois, partout dans la caisse (ecrans et recu) :
 * 123450 -> « 1 234,50 $ » (espace insecable fine U+202F pour les milliers,
 * U+00A0 avant le $). Negatif : « -0,02 $ ».
 * @param {number} cents entier
 * @returns {string}
 */
export function formatMontant(cents) {
    if (!entier(cents)) throw new TypeError('formatMontant : cents entiers attendus');
    const signe = cents < 0 ? '-' : '';
    const abs = Math.abs(cents);
    const dollars = String(Math.floor(abs / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f');
    const reste = String(abs % 100).padStart(2, '0');
    return `${signe}${dollars},${reste}\u00a0$`;
}

/*
 * Integration dans app/App.jsx (branche « session ouverte et vente permise »,
 * a la place de <SessionScreen>). Seule zone commune aux deux PR.
 *   - PR 1.5a seule : <EcranVente> de vente/EcranVente.jsx, onEncaisser absent
 *     (bouton « Encaisser comptant » desactive, « Paiement : prochaine version »).
 *   - PR 1.5b seule : ouverture de la caisse (fond) si aucun quart ouvert, puis
 *     l'ecran actuel <SessionScreen> ; le paiement n'est atteignable que par ses
 *     tests (panier factice).
 *   - Apres les deux (fait par la PR mergee en second) : un useReducer(fluxCaisse)
 *     dans paiement/FluxCaisse.jsx : OUVERTURE -> <OuvertureCaisse>,
 *     VENTE -> <EcranVente onEncaisser={(panier) => dispatch ENCAISSER}>,
 *     PAIEMENT -> <EcranPaiement panier onRetourVente onVenteTerminee>,
 *     RECU -> <EcranRecu vente onNouvelleVente onReimprimer> ; App.jsx rend
 *     <FluxCaisse EcranVente={EcranVente} ...>.
 */
